"use client";

/* ═══════════════════════════════════════════════════════════════════════════
   Agent-run mockup — real DOM, server-rendered in its final "done" frame.
   GSAP (AgentRunTimeline) is loaded after first paint and rewinds/plays it.
   Everything that ever appears is already in the DOM at its final position;
   elements hidden in the final frame start at opacity 0 / visibility hidden.
   Animation hooks are `data-ar` keys (space-separated), queried with ~=.
   ═══════════════════════════════════════════════════════════════════════════ */

import { useRef, type CSSProperties, type RefObject } from "react";
import dynamic from "next/dynamic";
import { Bot, FileText, Search, GitBranch, Puzzle, Folder, FolderOpen, Check } from "lucide-react";
import { useAfterPaint } from "@/lib/motion/useAfterPaint";
import { AGENT_RUN, EDITOR_LINE_PX, type ModelId } from "./script";

const AgentRunTimeline = dynamic(() => import("./AgentRunTimeline"), { ssr: false });

const HIDDEN: CSSProperties = { opacity: 0, visibility: "hidden" };
const HAIRLINE = "1px solid rgba(148,163,184,0.08)";
const ACTIVITY = [FileText, Search, GitBranch, Bot, Puzzle];
const AGENT_ICON_INDEX = 3;
const ADDED_COUNT = AGENT_RUN.file.lines.filter((l) => l.added).length;
const BASE_COUNT = AGENT_RUN.file.lines.length - ADDED_COUNT;
const STATUS_COLOR: Partial<Record<keyof typeof AGENT_RUN.status, string>> = {
  handoff: AGENT_RUN.models.claude.color,
  done: "#10B981",
};

function ModelBadge({ id, hidden }: { id: ModelId; hidden?: boolean }) {
  const m = AGENT_RUN.models[id];
  return (
    <span data-ar={`badge-${id}`} className="ar-stack-item inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold"
      style={{ color: m.color, background: "rgba(148,163,184,0.06)", border: `1px solid color-mix(in srgb, ${m.color} 35%, transparent)`, ...(hidden ? HIDDEN : null) }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
      {m.label}
    </span>
  );
}

function TitleBar() {
  return (
    <div className="flex items-center px-3 md:px-4 py-2 gap-3" style={{ background: "#010409", borderBottom: HAIRLINE }}>
      <div className="flex gap-2 shrink-0">
        <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
        <div className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
        <div className="w-3 h-3 rounded-full bg-[#28C840]" />
      </div>
      <div className="flex-1 flex items-center justify-center gap-2 text-[11px] text-[#8B949E] min-w-0">
        <span className="max-sm:hidden">{AGENT_RUN.appName}</span>
        <span className="ar-stack">
          <ModelBadge id="capuchin" />
          <ModelBadge id="claude" hidden />
        </span>
      </div>
      <div className="shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px]"
        style={{ background: "rgba(148,163,184,0.06)", border: HAIRLINE }}>
        <span className="text-[#8B949E]">{AGENT_RUN.cost.label}</span>
        <span data-ar="cost" className="font-mono font-semibold tabular-nums text-[#E2E8F0] inline-block min-w-[5ch] text-right">
          ${AGENT_RUN.cost.end.toFixed(2)}
        </span>
      </div>
    </div>
  );
}

function FileTree() {
  return (
    <div className="hidden lg:block w-[170px] shrink-0 py-2 overflow-hidden" style={{ background: "#0D1117", borderRight: HAIRLINE }}>
      <div className="px-3 mb-2 text-[9px] font-bold uppercase tracking-wider text-[#6E7681]">{AGENT_RUN.treeTitle}</div>
      {AGENT_RUN.tree.map((f) => {
        const active = "active" in f && f.active;
        const open = "open" in f && f.open;
        const Icon = f.kind === "file" ? FileText : open ? FolderOpen : Folder;
        return (
          <div key={f.name} className="flex items-center gap-1 pr-2 py-[3px] text-[10px]" style={{
            paddingLeft: f.depth * 10 + 8,
            background: active ? "rgba(108,92,231,0.08)" : "transparent",
            color: active ? "#E2E8F0" : "#8B949E",
            borderLeft: `2px solid ${active ? "var(--mc-violet)" : "transparent"}`,
          }}>
            <Icon className="w-3 h-3 shrink-0" />
            <span className={`truncate ${f.kind !== "file" ? "font-semibold" : ""}`}>{f.name}</span>
            {active && <span data-ar="tree-mark" className="ml-auto text-[8px] font-bold text-[#10B981]">M</span>}
          </div>
        );
      })}
    </div>
  );
}

function Editor() {
  const { lines } = AGENT_RUN.file;
  return (
    <div className="flex-1 flex flex-col min-w-0">
      <div className="flex shrink-0" style={{ background: "#010409", borderBottom: HAIRLINE }}>
        <div className="flex items-center gap-1.5 px-4 py-2 text-[10px] font-medium" style={{ background: "#0D1117", color: "#E2E8F0", borderRight: HAIRLINE, borderTop: "2px solid var(--mc-violet)" }}>
          <FileText className="w-3 h-3" /> {AGENT_RUN.file.name}
        </div>
        <div className="max-sm:hidden flex items-center gap-1.5 px-4 py-2 text-[10px] font-medium text-[#6E7681]" style={{ borderRight: HAIRLINE }}>
          <FileText className="w-3 h-3" /> {AGENT_RUN.file.secondaryTab}
        </div>
      </div>

      {/* Mobile crops to the $except → shouldSkip() region (last 10 rows, past the 8px top padding). */}
      <div className="relative flex-1 overflow-hidden max-md:flex-none max-md:h-[208px]" style={{ background: "#0D1117" }}>
        <div className="flex py-2 font-mono text-[10px] max-md:-translate-y-[168px]" style={{ lineHeight: `${EDITOR_LINE_PX}px` }}>
          {/* Static gutter: row numbers never move; extra numbers appear as lines stream in. */}
          <div className="w-9 shrink-0 text-right pr-2 select-none text-[#6E7681]">
            {lines.map((_, i) => (
              <div key={i} data-ar={i >= BASE_COUNT ? "gutter-extra" : undefined} style={{ height: EDITOR_LINE_PX }}>{i + 1}</div>
            ))}
          </div>
          <div className="relative flex-1 min-w-0">
            {lines.map((line, i) => (
              <div key={i} data-ar={`line line-${i}`} className="relative flex whitespace-pre" style={{ height: EDITOR_LINE_PX }}>
                {line.added && (
                  <span data-ar={`flash flash-${i}`} className="absolute inset-0 pointer-events-none"
                    style={{ ...HIDDEN, background: "rgba(16,185,129,0.14)", boxShadow: "inset 2px 0 0 #10B981" }} />
                )}
                <span className="relative w-3 shrink-0 text-center" style={{ color: line.added ? "#10B981" : "transparent" }}>{line.added ? "+" : " "}</span>
                <span className="relative">
                  {line.tokens.map((t, ti) => <span key={ti} style={{ color: t.color || undefined }}>{t.text}</span>)}
                  {line.beforeSuffix && (
                    <span data-ar="before-suffix" style={HIDDEN}>
                      {line.beforeSuffix.map((t, ti) => <span key={ti} style={{ color: t.color }}>{t.text}</span>)}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentPanel() {
  const { plan, prompt } = AGENT_RUN;
  return (
    <div className="w-full md:w-[264px] shrink-0 flex flex-col max-md:order-first" style={{ background: "#010409", borderLeft: HAIRLINE }}>
      <div className="px-3 py-2 flex items-center gap-2" style={{ borderBottom: HAIRLINE }}>
        <Bot className="w-4 h-4" style={{ color: "var(--mc-teal)" }} />
        <span className="text-[10px] font-bold text-[#E2E8F0]">Agent</span>
      </div>

      <div className="flex-1 p-2.5 flex flex-col gap-3">
        {/* Prompt: every character is in the DOM, so the box never changes size while "typing". */}
        <div className="rounded-lg px-2.5 py-2 text-[11px] leading-[1.55] text-[#C9D1D9]"
          style={{ background: "rgba(108,92,231,0.08)", border: "1px solid rgba(108,92,231,0.22)" }}>
          {Array.from(prompt).map((ch, i) => (
            <span key={i} data-ar="prompt-char" className="relative">
              {ch}
              <span data-ar="caret" className="absolute -right-px top-[2px] bottom-[1px] w-[2px] rounded-full" style={{ ...HIDDEN, background: "var(--mc-teal)" }} />
            </span>
          ))}
        </div>

        <div>
          <div data-ar="plan-title" className="mb-1.5 text-[9px] font-bold uppercase tracking-wider text-[#8B949E]">{plan.title}</div>
          <ol className="flex flex-col gap-1">
            {plan.steps.map((s, i) => {
              const complex = "complex" in s && s.complex;
              return (
                <li key={i} data-ar="step" className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[10.5px] text-[#C9D1D9]"
                  style={{ background: complex ? "rgba(245,158,11,0.06)" : "rgba(148,163,184,0.04)", border: HAIRLINE }}>
                  <span className="ar-stack ar-stack-fill w-3.5 h-3.5 shrink-0">
                    <span data-ar={`step-pending step-pending-${i}`} className="ar-stack-item rounded-full" style={{ ...HIDDEN, border: "1.5px solid #484F58" }} />
                    <span data-ar={`step-done step-done-${i}`} className="ar-stack-item rounded-full flex items-center justify-center"
                      style={{ background: complex ? "var(--warning)" : "var(--mc-teal)" }}>
                      <Check className="w-2.5 h-2.5 text-[#010409]" strokeWidth={3.5} />
                    </span>
                  </span>
                  <span className="truncate">{s.text}</span>
                  {complex && (
                    <span data-ar="complex-tag" className="ml-auto shrink-0 px-1.5 py-px rounded text-[9px] font-semibold"
                      style={{ color: "var(--warning)", background: "rgba(245,158,11,0.12)" }}>
                      {plan.complexTag}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      <div className="max-md:hidden px-2.5 py-2" style={{ borderTop: HAIRLINE }}>
        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[10px]" style={{ background: "rgba(148,163,184,0.04)", border: HAIRLINE }}>
          <span className="text-[#6E7681]">{AGENT_RUN.composerPlaceholder}</span>
          <span className="ml-auto text-[8px] text-[#6E7681] px-1 py-0.5 rounded" style={{ background: "rgba(148,163,184,0.06)" }}>⌘K</span>
        </div>
      </div>
    </div>
  );
}

function StatusBar() {
  const entries = Object.entries(AGENT_RUN.status) as [keyof typeof AGENT_RUN.status, string][];
  return (
    <div className="relative flex items-center px-3 py-1 text-[9px] gap-4" style={{ background: "rgba(108,92,231,0.06)", borderTop: "1px solid rgba(148,163,184,0.06)" }}>
      <span data-ar="status-bar-ok" className="absolute inset-0 pointer-events-none" style={{ background: "rgba(16,185,129,0.1)" }} />
      <span className="ar-stack relative font-medium">
        {entries.map(([key, text]) => (
          <span key={key} data-ar={`status status-${key}`} className="ar-stack-item whitespace-nowrap"
            style={{ color: STATUS_COLOR[key] ?? "var(--mc-teal)", ...(key === "done" ? null : HIDDEN) }}>
            {text}
          </span>
        ))}
      </span>
      {AGENT_RUN.statusMeta.map((m) => <span key={m} className="relative max-sm:hidden text-[#6E7681]">{m}</span>)}
    </div>
  );
}

/** Leaf that owns the after-paint state, so flipping it doesn't re-render the mockup. */
function Motion({ scope }: { scope: RefObject<HTMLDivElement | null> }) {
  return useAfterPaint() ? <AgentRunTimeline scope={scope} /> : null;
}

export default function AgentRunMockup() {
  const scope = useRef<HTMLDivElement>(null);

  return (
    <div className="mt-10 mx-auto max-w-5xl text-left">
      <p className="sr-only">{AGENT_RUN.srDescription}</p>
      <div ref={scope} aria-hidden="true" className="relative rounded-2xl p-[1px]"
        style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.15), rgba(20,184,166,0.06) 40%, rgba(108,92,231,0.15))" }}>
        <div className="rounded-2xl overflow-hidden select-none" style={{ contain: "layout paint", background: "#0D1117", boxShadow: "0 50px 140px rgba(0,0,0,0.35), 0 0 80px rgba(108,92,231,0.05)" }}>
          <TitleBar />
          <div className="flex max-md:flex-col md:h-[412px]">
            <div className="hidden md:flex w-12 flex-col items-center py-2 gap-1 shrink-0" style={{ background: "#010409", borderRight: HAIRLINE }}>
              {ACTIVITY.map((Icon, i) => (
                <div key={i} className="w-10 h-10 flex items-center justify-center rounded-lg relative" style={{
                  background: i === AGENT_ICON_INDEX ? "rgba(108,92,231,0.1)" : "transparent",
                  color: i === AGENT_ICON_INDEX ? "var(--mc-teal)" : "#6E7681",
                }}>
                  <Icon className="w-4 h-4" />
                  {i === AGENT_ICON_INDEX && <div className="absolute left-0 top-2 bottom-2 w-[2px] rounded-r-full" style={{ background: "var(--mc-teal)" }} />}
                </div>
              ))}
            </div>
            <FileTree />
            <Editor />
            <AgentPanel />
          </div>
          <StatusBar />
        </div>
      </div>
      <Motion scope={scope} />
    </div>
  );
}
